import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

// CSS
import "./employeePicker.css";

// Components
import SearchBar from "../Other Components/searchBar";
import ItemCard from "../Item Components/itemCard";
import PageLoader from "../Other Components/pageLoader";
import Title from "../Other Components/Title";

// Functions and hooks
import { GetEmployees } from "../../BackendAccess/EmployeeHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import { useTranslation } from "react-i18next";

export default function EmployeePicker({
  title = "Employees",
  onChange = (selectedEmployees) => {},
  initSelectedEmployees,
  singleSelect = false,
  disabled,
  className,
}) {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);

  const finalClassName = twMerge("employee-picker w-100 h-100", className);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);

  const containerRef = useRef(null);

  const { showNotification } = useNotification();

  useErrorHandler(error, showNotification, setPageError);

  const { t } = useTranslation();

  useEffect(() => {
    if (employees.length === 0) GetAllEmployees();

    if (initSelectedEmployees && initSelectedEmployees.length > 0)
      setSelectedEmployees(initSelectedEmployees);
  }, [initSelectedEmployees]);

  useEffect(() => {
    onChange(selectedEmployees);
  }, [selectedEmployees]);

  const GetAllEmployees = async () => {
    setLoading(true);
    try {
      const [data, error] = await GetEmployees();

      if (error) {
        error.isPageError = true;
        setError(error);
      } else {
        setEmployees(data);
      }
    } catch (error) {
      setError({
        err: t("server.Server error", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (term) => {
    if (!disabled) setSearchTerm(term);
  };

  const handleSearchFocus = () => {
    if (!disabled) setSearching(true);
  };

  const handleSearchBlur = (e) => {
    if (!containerRef.current.contains(e.relatedTarget) && !disabled) {
      setSearching(false);
    }
  };

  const handleClickOutside = (e) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.target) &&
      !disabled
    ) {
      setSearching(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const filteredEmployees = employees.filter((item) => {
    const lowerCaseTerm = searchTerm.toLowerCase();
    return (
      (item.FirstName + " " + item.LastName)
        .toLowerCase()
        .includes(lowerCaseTerm) ||
      (item.Username || "").toLowerCase().includes(lowerCaseTerm) ||
      item.UserID.toString().toLowerCase().includes(lowerCaseTerm)
    );
  });

  const sortedEmployees = [...filteredEmployees].sort((a, b) =>
    (a.FirstName + " " + a.LastName).toLowerCase() >
    (b.FirstName + " " + b.LastName).toLowerCase()
      ? 1
      : -1
  );

  const onEmployeeSelectChanged = (id, state) => {
    if (!disabled) {
      var newSelectedIds = [...selectedEmployees];
      if (state) {
        if (!singleSelect || newSelectedIds.length === 0) {
          newSelectedIds.push(id);
        } else if (newSelectedIds.length !== 0 && singleSelect) {
          newSelectedIds = [];
          newSelectedIds.push(id);
        }
      } else {
        newSelectedIds = newSelectedIds.filter((item) => item !== id);
      }
      setSelectedEmployees(newSelectedIds);
    }
  };

  const createCardProps = (item) => {
    return [
      { Name: "ID", Value: item.UserID, columnWidth: 1 },
      {
        Name: "Name",
        Value: item.FirstName + " " + item.LastName,
        columnWidth: 3,
      },
    ];
  };

  return (
    <div className={finalClassName} ref={containerRef} tabIndex={-1}>
      {disabled ? <div className="disabledDimmingCover"></div> : null}
      <Title className="h-auto w-100 font-l mb-1">{title}</Title>
      <div className="employee-picker-content">
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onFocus={handleSearchFocus}
          onBlur={handleSearchBlur}
          shadow={false}
          placeholder={t("Search employees...", { ns: "employees" })}
        />

        <div className="employee-picker-lists-container">
          <PageLoader loading={loading} error={pageError}>
            <ul
              className={
                searching
                  ? "employee-picker-list"
                  : "employee-picker-list collapsed"
              }
            >
              {sortedEmployees.map((item) => (
                <li
                  key={item.UserID}
                  className="employee-picker-list-item"
                  onClick={handleSearchFocus}
                >
                  <ItemCard
                    key={item.UserID}
                    ItemID={item.UserID}
                    propertiesToShow={createCardProps(item)}
                    bBtnVisible={false}
                    bBodyClickable={true}
                    bBtnSectionVisible={false}
                    bSelectable={true}
                    onSelected={() => {}}
                    selectedItemIds={selectedEmployees}
                    setIDSelected={onEmployeeSelectChanged}
                  />
                </li>
              ))}
            </ul>

            <ul className="picked-employees-list">
              {!loading && selectedEmployees.length > 0 ? (
                selectedEmployees.map((UserID) => {
                  const employee = employees.find((cl) => cl.UserID === UserID);
                  return employee ? (
                    <li key={UserID} className="picked-employees-list-item">
                      <ItemCard
                        key={employee.UserID}
                        ItemID={employee.UserID}
                        propertiesToShow={createCardProps(employee)}
                        bBtnVisible={false}
                        bBodyClickable={true}
                        bBtnSectionVisible={false}
                        bSelectable={true}
                        onSelected={() => {}}
                        selectedItemIds={selectedEmployees}
                        setIDSelected={onEmployeeSelectChanged}
                      />
                    </li>
                  ) : null;
                })
              ) : (
                <p className="cardPlaceholder Employee">
                  {t("No selected employees", { ns: "employees" })}
                </p>
              )}
            </ul>
          </PageLoader>
        </div>
      </div>
    </div>
  );
}
